import React, { useEffect } from "react";
import { connect } from "react-redux";
import { compose } from "recompose";
import { FormattedMessage, useIntl } from "react-intl";

import { Api } from "@api/index";
import { connectToApi } from "@api/components/connectToApi";
import { messages } from "@app/messages";
import { GlobalState } from "@app/models/state";
import { setTitle, TitleArgument } from "@app/util";
import { messages as musicMessages } from "@music/messages";
import { PropsWithId, makeGetAlbum, canUpdateAlbum as canUpdateAlbumSelector } from "@music/selectors";
import { Iframe } from "@oldwata/components/Iframe";
import { BreadcrumbsItem } from "@ui/Breadcrumbs";
import { Page } from "@ui/Page";
import { messages as uiMessages } from "@ui/messages";
import { albumNotFound, HasAlbum } from "@app/components/notFoundHocs";

interface AlbumsEditStateProps extends HasAlbum {
    canUpdateAlbum: boolean;
}

type AlbumsEditProps = PropsWithId & AlbumsEditStateProps;

const AlbumsEdit = ({ params: { id }, canUpdateAlbum, album }: AlbumsEditProps) => {
    const intl = useIntl();
    useEffect(() => {
        const title: TitleArgument[] = [messages.pageAlbumsTitle];
        if (album != null) {
            title.push(intl.formatMessage(uiMessages.editEntity, {
                entity: album.title,
            }));
        }
        setTitle(intl, ...title);
    }, [album]);

    const breadcrumbItems: BreadcrumbsItem[] = [
        { text: <FormattedMessage {...musicMessages.albumEntitySingular} /> },
        { text: <FormattedMessage {...uiMessages.buttonEdit} /> },
    ];
    if (album != null) {
        breadcrumbItems[0] = { to: album.getViewUrl(), text: album.title };
    }

    const path = "/album/edit/id/" + id + "?newwata=true";
    return (
        <Page style="blank" className="albums-edit-page" breadcrumbs={breadcrumbItems}>
            {canUpdateAlbum &&
                <Iframe path={path} />
            }
        </Page>
    );
};

// Map the users state to the component.
const makeMapStateToProps = () => {
    const getAlbum = makeGetAlbum();
    return (state: GlobalState, ownProps: PropsWithId) => {
        const album = getAlbum(state, ownProps);
        return {
            album,
            lastLoaded: [ album ? album.lastLoaded : null ],
            canUpdateAlbum: canUpdateAlbumSelector(state),
        } as AlbumsEditStateProps;
    };
};

const makeAlbumsShow = (id: number) => () => {
    return Api.albumsShow(id);
};

const ConnectedAlbumsEdit = compose<AlbumsEditProps, PropsWithId>(
    connect(makeMapStateToProps, null),
    connectToApi((props: PropsWithId) => {
        return {
            actionCreators: [ makeAlbumsShow(Number(props.params.id)) ],
            showRefresh: false,
        };
    }),
    albumNotFound,
)(AlbumsEdit);

export {
    AlbumsEdit as AlbumsEditPresenter,
    ConnectedAlbumsEdit as AlbumsEdit,
};
